/**
 * InventoryAgeReport — mini-grafico a barre "Età media attrezzi per negozio",
 * mostrato in fondo alla pagina Magazzino (backlog idea_a79cc7b08ce9).
 * Dati: GET /inventory/age-report (purchase_date degli item attivi, scoped per ruolo).
 */
import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Hourglass, Loader2 } from 'lucide-react';

function ageLabel(months) {
  if (months == null) return 'n/d';
  if (months < 12) return `${Math.round(months)} mesi`;
  const years = Math.floor(months / 12);
  const rem = Math.round(months % 12);
  return rem > 0 ? `${years}a ${rem}m` : `${years} anni`;
}

function tone(months) {
  if (months == null) return { bar: 'bg-slate-300', text: 'text-slate-500' };
  if (months < 24) return { bar: 'bg-emerald-500', text: 'text-emerald-700' };
  if (months < 48) return { bar: 'bg-amber-500', text: 'text-amber-700' };
  return { bar: 'bg-red-500', text: 'text-red-700' };
}

export default function InventoryAgeReport() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get('/inventory/age-report')
      .then(r => setRows(r.data))
      .catch(() => setRows([]));
  }, []);

  const max = Math.max(...(rows || []).map(r => r.avg_age_months || 0), 1);

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5" data-testid="age-report-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <Hourglass className="w-5 h-5" /> Età media attrezzi per negozio
        </h3>
        <p className="text-[11px] text-slate-400">
          Dalla data di acquisto degli strumenti attivi · <span className="text-emerald-600 font-semibold">&lt; 2 anni</span> · <span className="text-amber-600 font-semibold">2–4 anni</span> · <span className="text-red-600 font-semibold">&gt; 4 anni</span>
        </p>
      </div>
      {rows === null ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8" data-testid="age-report-empty">
          Nessuno strumento con negozio assegnato.
        </p>
      ) : (
        <div className="space-y-2.5">
          {rows.map(r => {
            const t = tone(r.avg_age_months);
            return (
              <div key={r.store_id} className="flex items-center gap-3" data-testid={`age-report-row-${r.store_id}`}>
                <div className="w-44 shrink-0 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{r.store_name}</p>
                  <p className="text-[10px] text-slate-400">
                    {r.items_count} strumenti{r.items_with_date < r.items_count ? ` · ${r.items_count - r.items_with_date} senza data` : ''}
                  </p>
                </div>
                <div className="flex-1 h-4 bg-slate-100">
                  <div className={`h-4 ${t.bar}`} style={{ width: `${Math.max(((r.avg_age_months || 0) / max) * 100, 2)}%` }} />
                </div>
                <span className={`w-16 text-right text-sm font-semibold tabular-nums ${t.text}`}>{ageLabel(r.avg_age_months)}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
